import { select } from "d3";

import {
  AppletElement,
  EDGE_STROKE,
  EdgeEndpoints,
  GroupSelection,
  NODE_STROKE,
  Point,
  SvgSelection,
  renderApplet,
  trimmedEdgeEndpoints,
} from "./common";

type StarNodeKind = "center" | "leaf";
type StarNode = Readonly<{
  id: string;
  label: number;
  x: number;
  y: number;
  kind: StarNodeKind;
}>;

type StarEdge = Readonly<{
  source: string;
  target: string;
}>;

type StarPanel = Readonly<{
  originX: number;
  complement: boolean;
}>;

const STAR_EDGE_COUNT = 5;
const STAR_PANEL_WIDTH = 360;
const STAR_WIDTH = STAR_PANEL_WIDTH * 2;
const STAR_HEIGHT = 285;
const CENTER_RADIUS = 22;
const LEAF_RADIUS = 16;
const CENTER_FILL = "#8fbc8f";
const LEAF_FILL = "#c9ddc5";
const VERTEX_LABEL_FILL = "#263726";
const EDGE_LABEL_FILL = "#666";
const EDGE_LABEL_BACKGROUND_FILL = "white";

const STAR_NODES: readonly StarNode[] = [
  { id: "c", label: 0, x: 180, y: 146, kind: "center" },
  { id: "l1", label: 1, x: 180, y: 58, kind: "leaf" },
  { id: "l2", label: 2, x: 286, y: 112, kind: "leaf" },
  { id: "l3", label: 3, x: 246, y: 232, kind: "leaf" },
  { id: "l4", label: 4, x: 114, y: 232, kind: "leaf" },
  { id: "l5", label: 5, x: 74, y: 112, kind: "leaf" },
];

const STAR_EDGES: readonly StarEdge[] = [
  { source: "c", target: "l1" },
  { source: "c", target: "l2" },
  { source: "c", target: "l3" },
  { source: "c", target: "l4" },
  { source: "c", target: "l5" },
];

const STAR_PANELS: readonly StarPanel[] = [
  { originX: 0, complement: false },
  { originX: STAR_PANEL_WIDTH, complement: true },
];

function nodeLookup(): ReadonlyMap<string, StarNode> {
  return new Map(STAR_NODES.map((node: StarNode): [string, StarNode] => [node.id, node]));
}

function nodeRadius(node: StarNode): number {
  return node.kind === "center" ? CENTER_RADIUS : LEAF_RADIUS;
}

function nodeFill(node: StarNode): string {
  return node.kind === "center" ? CENTER_FILL : LEAF_FILL;
}

function edgeEndpoints(edge: StarEdge, nodes: ReadonlyMap<string, StarNode>): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(source, target, nodeRadius(source), nodeRadius(target));
}

function panelNodeLabel(panel: StarPanel, node: StarNode): number {
  return panel.complement ? STAR_EDGE_COUNT - node.label : node.label;
}

function edgeLabel(edge: StarEdge, nodes: ReadonlyMap<string, StarNode>, panel: StarPanel): string {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return "";
  }

  return Math.abs(panelNodeLabel(panel, source) - panelNodeLabel(panel, target)).toString();
}

function edgeLabelPosition(edge: StarEdge, nodes: ReadonlyMap<string, StarNode>): Point {
  const endpoints = edgeEndpoints(edge, nodes);
  const x = (endpoints.x1 + endpoints.x2) * 0.5;
  const y = (endpoints.y1 + endpoints.y2) * 0.5;

  return { x, y };
}

function appendSvg(element: AppletElement): SvgSelection {
  return select<AppletElement, unknown>(element)
    .append<SVGSVGElement>("svg")
    .attr("viewBox", `0 0 ${STAR_WIDTH} ${STAR_HEIGHT}`)
    .attr("width", STAR_WIDTH)
    .attr("height", STAR_HEIGHT)
    .attr("role", "img")
    .attr("aria-label", "Two graceful labelings of the star K one five: one with center zero and one complementary labeling with center five.");
}

function renderEdges(graph: GroupSelection): void {
  const nodes = nodeLookup();

  graph.append<SVGGElement>("g")
    .attr("class", "star-example-edges")
    .selectAll<SVGLineElement, StarEdge>("line")
    .data(STAR_EDGES)
    .join("line")
    .attr("x1", (edge: StarEdge): number => edgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: StarEdge): number => edgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: StarEdge): number => edgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: StarEdge): number => edgeEndpoints(edge, nodes).y2)
    .attr("stroke", EDGE_STROKE)
    .attr("stroke-width", 1.25)
    .attr("stroke-linecap", "round");
}

function renderEdgeLabels(graph: GroupSelection, panel: StarPanel): void {
  const nodes = nodeLookup();
  const edgeLabels = graph.append<SVGGElement>("g")
    .attr("class", "star-example-edge-labels")
    .selectAll<SVGGElement, StarEdge>("g")
    .data(STAR_EDGES)
    .join("g")
    .attr("transform", (edge: StarEdge): string => {
      const point = edgeLabelPosition(edge, nodes);
      return `translate(${point.x},${point.y})`;
    });

  edgeLabels.append<SVGCircleElement>("circle")
    .attr("r", 11)
    .attr("fill", EDGE_LABEL_BACKGROUND_FILL);

  edgeLabels.append<SVGTextElement>("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("font-family", "Avenir Next, Helvetica, Arial, sans-serif")
    .attr("font-size", 12)
    .attr("fill", EDGE_LABEL_FILL)
    .text((edge: StarEdge): string => edgeLabel(edge, nodes, panel));
}

function renderNodes(graph: GroupSelection, panel: StarPanel): void {
  const nodeGroups = graph.append<SVGGElement>("g")
    .attr("class", "star-example-nodes")
    .selectAll<SVGGElement, StarNode>("g")
    .data(STAR_NODES)
    .join("g")
    .attr("transform", (node: StarNode): string => `translate(${node.x},${node.y})`);

  nodeGroups.append<SVGCircleElement>("circle")
    .attr("r", (node: StarNode): number => nodeRadius(node))
    .attr("fill", (node: StarNode): string => nodeFill(node))
    .attr("stroke", NODE_STROKE)
    .attr("stroke-width", (node: StarNode): number => (node.kind === "center" ? 1.8 : 1.3));

  nodeGroups.append<SVGTextElement>("text")
    .attr("text-anchor", "middle")
    .attr("dy", "0.33em")
    .attr("font-family", "Times, serif")
    .attr("font-size", (node: StarNode): number => (node.kind === "center" ? 18 : 15))
    .attr("fill", VERTEX_LABEL_FILL)
    .text((node: StarNode): string => panelNodeLabel(panel, node).toString());
}

function renderPanel(svg: SvgSelection, panel: StarPanel): void {
  const graph = svg.append<SVGGElement>("g")
    .attr("class", "star-example-graph")
    .attr("transform", `translate(${panel.originX},0)`);

  renderEdges(graph);
  renderEdgeLabels(graph, panel);
  renderNodes(graph, panel);
}

function renderStarExample(element: AppletElement): void {
  element.dataset.rendered = "true";
  element.replaceChildren();

  const svg = appendSvg(element);
  STAR_PANELS.forEach((panel: StarPanel): void => renderPanel(svg, panel));
}

renderApplet(".star-example-applet", renderStarExample);
